import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import UserSideNav from "../../components/user/UserSideNav";
import UserHeader from "../../components/user/UserHeader";
import { UserRound, Lock, CheckCircle2, ExternalLink, Landmark, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { usersApi } from "../../services/api";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "" });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [pwMsg, setPwMsg] = useState(null);
  const [payoutStatus, setPayoutStatus] = useState(user?.payoutOnboardingStatus || "not_started");
  const [payoutCountry, setPayoutCountry] = useState(user?.payoutCountry || "US");
  const [loadingPayout, setLoadingPayout] = useState(true);
  const [startingPayout, setStartingPayout] = useState(false);
  const [payoutMsg, setPayoutMsg] = useState(null);

  useEffect(() => {
    const returned = searchParams.get("payout");
    usersApi.getPayoutAccountStatus()
      .then(async ({ data }) => {
        setPayoutStatus(data.data.status);
        if (data.data.country) setPayoutCountry(data.data.country);
        if (returned === "return") {
          setPayoutMsg({
            type: data.data.status === "active" ? "success" : "error",
            text: data.data.status === "active"
              ? "Your payout account is ready to receive winnings."
              : "Stripe still needs information before payouts can be released.",
          });
        }
        await refreshUser();
      })
      .catch((err) => setPayoutMsg({ type: "error", text: err.response?.data?.message || "Unable to load payout status." }))
      .finally(() => {
        setLoadingPayout(false);
        if (returned) setSearchParams({}, { replace: true });
      });
  }, []);

  function handleChange(e) { setForm((f) => ({ ...f, [e.target.name]: e.target.value })); }
  function handlePwChange(e) { setPwForm((f) => ({ ...f, [e.target.name]: e.target.value })); }

  async function handleProfileSave(e) {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    try {
      await usersApi.updateMyProfile(form);
      await refreshUser();
      setProfileMsg({ type: "success", text: "Profile updated successfully." });
    } catch (err) {
      setProfileMsg({ type: "error", text: err.response?.data?.message || "Failed to update profile." });
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordSave(e) {
    e.preventDefault();
    if (pwForm.newPassword.length < 8) {
      setPwMsg({ type: "error", text: "New password must be at least 8 characters." });
      return;
    }
    setSavingPw(true);
    setPwMsg(null);
    try {
      await usersApi.changePassword(pwForm);
      setPwForm({ currentPassword: "", newPassword: "" });
      setPwMsg({ type: "success", text: "Password changed successfully." });
    } catch (err) {
      setPwMsg({ type: "error", text: err.response?.data?.message || "Failed to change password." });
    } finally {
      setSavingPw(false);
    }
  }

  async function handlePayoutSetup() {
    if (!/^[A-Za-z]{2}$/.test(payoutCountry)) {
      setPayoutMsg({ type: "error", text: "Enter a valid two-letter country code." });
      return;
    }
    setStartingPayout(true);
    setPayoutMsg(null);
    try {
      const { data } = await usersApi.startPayoutOnboarding(payoutCountry.toUpperCase());
      window.location.assign(data.data.url);
    } catch (err) {
      setPayoutMsg({ type: "error", text: err.response?.data?.message || "Unable to start Stripe payout setup." });
      setStartingPayout(false);
    }
  }

  return (
    <div className="user-shell">
      <UserSideNav />
      <div className="user-app">
        <UserHeader />
        <main className="user-main">
          <div className="welcome-block">
            <div>
              <div className="page-kicker">Profile</div>
              <h1>Account Settings</h1>
              <p>Update your personal information and security settings.</p>
            </div>
          </div>

          <div style={{ display: "grid", gap: 32, maxWidth: 560 }}>
            {/* Personal info */}
            <section className="section-block">
              <div className="section-heading">
                <h2><UserRound size={20} /> Personal Info</h2>
              </div>
              <div className="admin-panel" style={{ padding: 24 }}>
                {profileMsg && (
                  <div className={profileMsg.type === "success" ? "auth-success" : "auth-error"} style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                    {profileMsg.type === "success" && <CheckCircle2 size={14} />} {profileMsg.text}
                  </div>
                )}
                <form style={{ display: "grid", gap: 20 }} onSubmit={handleProfileSave}>
                  <div className="auth-field">
                    <label htmlFor="p-name">Full Name</label>
                    <input id="p-name" name="name" type="text" value={form.name} onChange={handleChange} />
                  </div>
                  <div className="auth-field">
                    <label>Email <span className="auth-optional">(read-only)</span></label>
                    <input type="email" value={user?.email || ""} readOnly style={{ opacity: 0.5, cursor: "not-allowed" }} />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="p-phone">Phone</label>
                    <input id="p-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+1 555 000 0000" />
                  </div>
                  <button type="submit" className="auth-submit" style={{ maxWidth: 180 }} disabled={saving}>
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                </form>
              </div>
            </section>

            {/* Change password */}
            <section className="section-block">
              <div className="section-heading">
                <h2><Lock size={20} /> Change Password</h2>
              </div>
              <div className="admin-panel" style={{ padding: 24 }}>
                {pwMsg && (
                  <div className={pwMsg.type === "success" ? "auth-success" : "auth-error"} style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                    {pwMsg.type === "success" && <CheckCircle2 size={14} />} {pwMsg.text}
                  </div>
                )}
                <form style={{ display: "grid", gap: 20 }} onSubmit={handlePasswordSave}>
                  <div className="auth-field">
                    <label htmlFor="cur-pw">Current Password</label>
                    <input id="cur-pw" name="currentPassword" type="password" value={pwForm.currentPassword} onChange={handlePwChange} placeholder="••••••••" />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="new-pw">New Password</label>
                    <input id="new-pw" name="newPassword" type="password" value={pwForm.newPassword} onChange={handlePwChange} placeholder="Min. 8 characters" />
                  </div>
                  <button type="submit" className="auth-submit" style={{ maxWidth: 200 }} disabled={savingPw}>
                    {savingPw ? "Updating…" : "Update Password"}
                  </button>
                </form>
              </div>
            </section>

            <section className="section-block">
              <div className="section-heading">
                <h2><Landmark size={20} /> Winnings Payouts</h2>
              </div>
              <div className="admin-panel" style={{ padding: 24 }}>
                {payoutMsg && (
                  <div className={payoutMsg.type === "success" ? "auth-success" : "auth-error"} style={{ marginBottom: 16 }}>
                    {payoutMsg.text}
                  </div>
                )}
                <div className="payout-account-head">
                  <div>
                    <strong>Stripe payout account</strong>
                    <p>Stripe securely collects identity and bank information. Lucky Pool does not store those details.</p>
                  </div>
                  <span className={`table-status ${payoutStatus === "active" ? "live" : payoutStatus === "restricted" ? "danger" : "warn"}`}>
                    {loadingPayout ? "checking" : payoutStatus.replaceAll("_", " ")}
                  </span>
                </div>
                {payoutStatus === "not_started" && !loadingPayout && (
                  <div className="auth-field" style={{ marginTop: 18 }}>
                    <label htmlFor="payout-country">Account country</label>
                    <input id="payout-country" value={payoutCountry} maxLength={2} onChange={(event) => setPayoutCountry(event.target.value.toUpperCase())} placeholder="US" />
                  </div>
                )}
                {payoutStatus !== "active" && (
                  <button type="button" className="auth-submit" style={{ maxWidth: 240 }} onClick={handlePayoutSetup} disabled={loadingPayout || startingPayout}>
                    {startingPayout || loadingPayout ? <Loader2 size={16} className="spin" /> : <ExternalLink size={16} />}
                    {payoutStatus === "not_started" ? "Set Up Payouts" : "Continue Stripe Setup"}
                  </button>
                )}
                {payoutStatus === "active" && (
                  <div className="auth-success" style={{ marginTop: 18, display: "flex", gap: 8, alignItems: "center" }}>
                    <CheckCircle2 size={15} /> Ready to receive winnings through Stripe.
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
